import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  Download,
  MapPin,
  MessageCircle,
  PhoneCall,
  ShieldCheck,
  Video,
  Flame,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppDownloadButton from '../components/AppDownloadButton';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import PhoneShowcase from '../components/PhoneShowcase';
import { supabase } from '../lib/supabase';
import { WORKER_WHATSAPP_LINK } from '../constants/externalLinks';

type LocaleKey = 'en' | 'hinglish' | 'hi';

const WHATSAPP_LINK = WORKER_WHATSAPP_LINK;
const WHATSAPP_DISPLAY = '+91 9408188954';
const TRUST_NAMES = ['Chelvi Engineers and Consultant', 'L&T'];

const trackImages = {
  local: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=1200&auto=format&fit=crop',
  abroad: 'https://images.unsplash.com/photo-1513828583688-c52646db42da?q=80&w=1200&auto=format&fit=crop',
};

type LiveJob = {
  id: string;
  role?: string | null;
  photo_url?: string | null;
  description?: string | null;
  salary_min?: number | null;
  salary_max?: number | null;
  currency?: string | null;
  job_type?: string | null;
  skill_category?: string | null;
  is_urgent?: boolean | null;
  status?: string | null;
  created_at?: string | null;
  campaign?: {
    id?: string | null;
    company_name?: string | null;
    country?: string | null;
    status?: string | null;
    poster_urls?: string[] | null;
    recruiter?: {
      name?: string | null;
      company_name?: string | null;
      whatsapp_number?: string | null;
      logo_url?: string | null;
      banner_url?: string | null;
      is_verified?: boolean | null;
      status?: string | null;
    } | Array<{
      name?: string | null;
      company_name?: string | null;
      whatsapp_number?: string | null;
      logo_url?: string | null;
      banner_url?: string | null;
      is_verified?: boolean | null;
      status?: string | null;
    }> | null;
  } | Array<{
    id?: string | null;
    company_name?: string | null;
    country?: string | null;
    status?: string | null;
    poster_urls?: string[] | null;
    recruiter?: {
      name?: string | null;
      company_name?: string | null;
      whatsapp_number?: string | null;
      logo_url?: string | null;
      banner_url?: string | null;
      is_verified?: boolean | null;
      status?: string | null;
    } | Array<{
      name?: string | null;
      company_name?: string | null;
      whatsapp_number?: string | null;
      logo_url?: string | null;
      banner_url?: string | null;
      is_verified?: boolean | null;
      status?: string | null;
    }> | null;
  }> | null;
};

const content: Record<
  LocaleKey,
  {
    eyebrow: string;
    title: string;
    subtitle: string;
    downloadCta: string;
    webCta: string;
    whatsappCta: string;
    trustPills: string[];
    trustHeading: string;
    trustCopy: string;
    trustBullets: string[];
    disclaimerTitle: string;
    disclaimerBody: string;
    tracksTitle: string;
    localTitle: string;
    localBody: string;
    abroadTitle: string;
    abroadBody: string;
    processTitle: string;
    processSteps: Array<{ title: string; body: string }>;
    proofTitle: string;
    proofBody: string;
    proofBullets: string[];
    rootTitle: string;
    rootBody: string;
    recognizedNames: string;
    jobsTitle: string;
    jobsBody: string;
    jobsEmpty: string;
  }
> = {
  en: {
    eyebrow: 'Trust & Skill Identity for Workers',
    title: 'Jobisa - Aapke Hunar ki Pehchan',
    subtitle:
      'Build your digital skill identity. Get discovered by top contractors in India and by verified agencies for abroad opportunities. Free profile hosting for workers.',
    downloadCta: 'Download the App',
    webCta: 'Open Web Jobs',
    whatsappCta: 'Chat on WhatsApp',
    trustPills: ['Video CV onboarding', 'Free profile hosting', 'WhatsApp support'],
    trustHeading: 'Built for workers who want skill-first discovery',
    trustCopy:
      'Jobisa is designed for workers who may not have polished resumes but do have real site skill. Your profile, video, trade, location, and work preference become a practical digital identity.',
    trustBullets: [
      'Workers can be discovered for India roles and abroad roles from the same profile.',
      'The product is built around practical worker proof: role, skills, video, location, and work preference.',
      'WhatsApp and app onboarding reduce typing and help workers get started faster.',
    ],
    disclaimerTitle: 'Honest platform position',
    disclaimerBody:
      'Jobisa is a technology platform. We are not a recruitment agency. We provide tools for workers to show their skills and for companies, contractors, and licensed recruitment partners to find them directly.',
    tracksTitle: 'A clear career ladder',
    localTitle: 'Local Jobs in India',
    localBody:
      'Focus on work in industrial clusters and GIDC hubs such as Vadodara, Ahmedabad, and Pune, with direct contact routes to site teams and hiring coordinators.',
    abroadTitle: 'Abroad Jobs through verified partners',
    abroadBody:
      'Connect with licensed recruitment partners for Gulf and Europe opportunities. No fake promises, no hidden file pushing, and a stronger skill-first profile through video and work history.',
    processTitle: 'A no-typing onboarding path',
    processSteps: [
      {
        title: 'Send a short skill video',
        body: 'Send a 15-second welding, fabrication, or trade video on WhatsApp or through the app.',
      },
      {
        title: 'Get a digital Hunar Card',
        body: 'Jobisa converts skill, role, experience, and media into a simple digital worker identity.',
      },
      {
        title: 'Get direct contact',
        body: 'Companies or recruitment partners can shortlist, call, and move the worker toward a site test or interview.',
      },
    ],
    proofTitle: 'Why this feels more credible',
    proofBody:
      'Jobisa is presented as an early-stage Made-in-India workforce technology platform, not as a giant placement company. That is the right posture for trust, compliance, and brand approval.',
    proofBullets: [
      'Free profile hosting for workers. We do not position this as "100% free jobs."',
      'Worker data is used for direct job matching, not sold to third-party marketing agencies.',
      'Workers can start on WhatsApp and continue in the app.',
    ],
    rootTitle: 'Starting from Vadodara, built for workforce mobility',
    rootBody:
      'Jobisa is being built from Vadodara, Gujarat as a digital workforce infrastructure layer focused on credible worker identity, verified recruiter access, and practical hiring workflows.',
    recognizedNames: 'By top recruiters',
    jobsTitle: 'Live jobs on the platform',
    jobsBody: 'A few current roles are shown below directly from the database so workers can immediately understand what kind of demand exists.',
    jobsEmpty: 'Jobs will appear here once active roles are published.',
  },
  hinglish: {
    eyebrow: 'Workers ke liye trust aur skill identity',
    title: 'Jobisa - Aapke Hunar ki Pehchan',
    subtitle:
      'Apni digital skill identity banao. India ke top contractors aur abroad ke verified agencies tak pahunch pao. Workers ke liye free profile hosting.',
    downloadCta: 'App Download Karein',
    webCta: 'Web Jobs Kholein',
    whatsappCta: 'WhatsApp Par Baat Karein',
    trustPills: ['Video CV onboarding', 'Free profile hosting', 'WhatsApp support'],
    trustHeading: 'Un workers ke liye jo resume se zyada skill dikhana chahte hain',
    trustCopy:
      'Jobisa un workers ke liye hai jinke paas asli site skill hai, chahe formal CV strong na ho. Profile, video, trade, location aur work preference milkar unki digital identity banate hain.',
    trustBullets: [
      'Ek hi profile se India aur abroad dono type ke roles ke liye discover kiya ja sakta hai.',
      'Worker proof role, skill, video aur location ke saath build hota hai.',
      'App aur WhatsApp onboarding typing kam karte hain aur setup fast banate hain.',
    ],
    disclaimerTitle: 'Seedhi aur honest positioning',
    disclaimerBody:
      'Jobisa ek technology platform hai. Hum recruitment agency nahi hain. Hum workers ko apna skill dikhane ke tools dete hain aur companies, contractors aur licensed recruitment partners ko unhe directly dhoondhne dete hain.',
    tracksTitle: 'Local se abroad tak ek practical growth path',
    localTitle: 'Local Jobs in India',
    localBody:
      'Vadodara, Ahmedabad aur Pune jaise industrial aur GIDC hubs ke roles, jahan site teams aur hiring coordinators tak seedha route ho sakta hai.',
    abroadTitle: 'Verified partners ke through abroad roles',
    abroadBody:
      'Gulf aur Europe ke roles ke liye licensed recruitment partners ke saath connect karna. Fake promise nahi, hidden file charges nahi, aur skill-first video profile.',
    processTitle: 'No-typing onboarding flow',
    processSteps: [
      {
        title: 'Short skill video bhejiye',
        body: '15-second welding, fabrication ya trade ka video WhatsApp ya app se bhejiye.',
      },
      {
        title: 'Digital Hunar Card ban jata hai',
        body: 'Jobisa skill, role, experience aur media ko simple digital worker identity mein convert karta hai.',
      },
      {
        title: 'Direct call ya shortlist',
        body: 'Company ya recruitment partner shortlist karke call, site test ya interview tak le ja sakte hain.',
      },
    ],
    proofTitle: 'Yeh zyada credible kyon lagta hai',
    proofBody:
      'Yeh site Jobisa ko ek early-stage Made-in-India workforce tech platform ke tarah position karti hai, koi bada placement company jaisa overclaim nahi karti.',
    proofBullets: [
      'Workers ke liye free profile hosting. Hum "100% free jobs" jaisa claim nahi karte.',
      'Worker data direct job matching ke liye hai, third-party marketing agencies ko bechne ke liye nahi.',
      'Worker WhatsApp se bhi shuru kar sakta hai.',
    ],
    rootTitle: 'Vadodara se shuru, workforce mobility ke liye build ho raha hai',
    rootBody:
      'Jobisa Vadodara, Gujarat se ek digital workforce infrastructure layer ke roop mein build ho raha hai, jahan focus credible worker identity, verified recruiter access aur practical hiring workflows par hai.',
    recognizedNames: 'Top recruiters ke saath',
    jobsTitle: 'Platform par live jobs',
    jobsBody: 'Neeche kuch current roles seedha database se aa rahe hain taki worker turant samajh sake ki demand kaisi hai.',
    jobsEmpty: 'Active roles publish hone par jobs yahan dikhengi.',
  },
  hi: {
    eyebrow: 'वर्कर्स के लिए भरोसेमंद स्किल पहचान',
    title: 'Jobisa - आपके हुनर की पहचान',
    subtitle:
      'अपनी डिजिटल स्किल पहचान बनाइए। भारत के contractors और abroad के verified agencies तक पहुँचिए। workers के लिए free profile hosting उपलब्ध है।',
    downloadCta: 'ऐप डाउनलोड करें',
    webCta: 'Web Jobs देखें',
    whatsappCta: 'WhatsApp पर संपर्क करें',
    trustPills: ['Video CV onboarding', 'Free profile hosting', 'WhatsApp support'],
    trustHeading: 'उन वर्कर्स के लिए जो अपना असली हुनर दिखाना चाहते हैं',
    trustCopy:
      'Jobisa उन workers के लिए बनाया गया है जिनके पास वास्तविक site skill है, भले ही polished resume न हो। Profile, video, trade, location और work preference मिलकर उनकी digital identity बनाते हैं।',
    trustBullets: [
      'एक ही profile से India और abroad दोनों तरह के roles के लिए discover किया जा सकता है।',
      'Worker proof role, skill, video और location के साथ build होता है।',
      'App और WhatsApp onboarding typing कम करते हैं और शुरुआत आसान बनाते हैं।',
    ],
    disclaimerTitle: 'ईमानदार प्लेटफॉर्म पोजिशनिंग',
    disclaimerBody:
      'Jobisa एक technology platform है। हम recruitment agency नहीं हैं। हम workers को अपना skill दिखाने के tools देते हैं और companies, contractors और licensed recruitment partners को उन्हें directly खोजने देते हैं।',
    tracksTitle: 'Local से abroad तक एक स्पष्ट career path',
    localTitle: 'भारत में local jobs',
    localBody:
      'Vadodara, Ahmedabad और Pune जैसे industrial और GIDC hubs में roles, जहाँ site teams और hiring coordinators तक सीधा route हो सकता है।',
    abroadTitle: 'Verified partners के जरिए abroad roles',
    abroadBody:
      'Gulf और Europe opportunities के लिए licensed recruitment partners से connect कीजिए। Fake promise नहीं, hidden file pushing नहीं, और skill-first video profile.',
    processTitle: 'कम typing वाला onboarding flow',
    processSteps: [
      {
        title: 'Short skill video भेजिए',
        body: '15-second welding, fabrication या trade video WhatsApp या app के जरिए भेजिए।',
      },
      {
        title: 'Digital Hunar Card बनता है',
        body: 'Jobisa skill, role, experience और media को एक simple digital worker identity में बदल देता है।',
      },
      {
        title: 'Direct call या shortlist',
        body: 'Company या recruitment partner shortlist करके call, site test या interview तक ले जा सकते हैं।',
      },
    ],
    proofTitle: 'यह अधिक भरोसेमंद क्यों लगता है',
    proofBody:
      'यह site Jobisa को एक early-stage Made-in-India workforce technology platform की तरह दिखाती है, किसी बड़े placement company की तरह overclaim नहीं करती।',
    proofBullets: [
      'Workers के लिए free profile hosting. हम "100% free jobs" जैसा claim नहीं करते।',
      'Worker data direct job matching के लिए है, third-party marketing agencies को बेचने के लिए नहीं।',
      `Worker ${WHATSAPP_DISPLAY} पर WhatsApp से भी शुरुआत कर सकता है।`,
    ],
    rootTitle: 'Vadodara से शुरुआत, workforce mobility के लिए निर्माण',
    rootBody:
      'Jobisa Vadodara, Gujarat से एक digital workforce infrastructure layer के रूप में बनाया जा रहा है, जहाँ focus credible worker identity, verified recruiter access और practical hiring workflows पर है।',
    recognizedNames: 'वह नाम जिन्हें workers hiring conversations में पहचानते हैं',
    jobsTitle: 'Platform पर live jobs',
    jobsBody: 'नीचे कुछ current roles सीधे database से दिखाए जा रहे हैं ताकि worker तुरंत समझ सके कि demand कैसी है।',
    jobsEmpty: 'Active roles publish होने पर jobs यहाँ दिखेंगी।',
  },
};

const SubtleOrangeGrid: React.FC = () => (
  <svg className="absolute inset-0 h-full w-full pointer-events-none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <pattern id="hero-grid-sm" width="36" height="36" patternUnits="userSpaceOnUse">
        <path d="M 36 0 L 0 0 0 36" fill="none" stroke="rgba(249,115,22,0.08)" strokeWidth="0.7" />
      </pattern>
      <pattern id="hero-grid-lg" width="180" height="180" patternUnits="userSpaceOnUse">
        <rect width="180" height="180" fill="url(#hero-grid-sm)" />
        <path d="M 180 0 L 0 0 0 180" fill="none" stroke="rgba(249,115,22,0.12)" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#hero-grid-lg)" />
  </svg>
);

const WorkerLandingPage: React.FC = () => {
  const { i18n } = useTranslation();
  const language = (['en', 'hinglish', 'hi'].includes(i18n.language) ? i18n.language : 'en') as LocaleKey;
  const copy = content[language];
  const [liveJobs, setLiveJobs] = useState<LiveJob[]>([]);

  useEffect(() => {
    let mounted = true;

    const fetchLiveJobs = async () => {
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id,
          role,
          photo_url,
          description,
          salary_min,
          salary_max,
          currency,
          job_type,
          skill_category,
          is_urgent,
          status,
          created_at,
          campaign:campaigns!inner (
            id,
            company_name,
            country,
            status,
            poster_urls,
            recruiter:recruiters (
              name,
              company_name,
              whatsapp_number,
              logo_url,
              banner_url,
              is_verified,
              status
            )
          )
        `)
        .in('status', ['open', 'active'])
        .eq('campaign.status', 'active')
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) {
        console.warn('Failed to fetch worker landing jobs:', error.message);
        return;
      }

      if (mounted) {
        setLiveJobs((data as LiveJob[]) || []);
      }
    };

    void fetchLiveJobs();

    return () => {
      mounted = false;
    };
  }, []);

  const normalizeCampaign = (job: LiveJob) => {
    if (Array.isArray(job.campaign)) return job.campaign[0] || null;
    return job.campaign || null;
  };

  const normalizeRecruiter = (job: LiveJob) => {
    const campaign = normalizeCampaign(job);
    if (!campaign) return null;
    const recruiter = campaign.recruiter;
    if (Array.isArray(recruiter)) return recruiter[0] || null;
    return recruiter || null;
  };

  const formatSalary = (job: LiveJob) => {
    if (!job.salary_min && !job.salary_max) return null;
    const currency = job.currency || 'INR';
    if (job.salary_min && job.salary_max) return `${currency} ${job.salary_min}-${job.salary_max}`;
    return `${currency} ${job.salary_min || job.salary_max}`;
  };

  const getJobImage = (job: LiveJob) => {
    if (job.photo_url) return job.photo_url;
    const campaign = normalizeCampaign(job);
    const recruiter = normalizeRecruiter(job);
    const poster = Array.isArray(campaign?.poster_urls) ? campaign?.poster_urls?.[0] : null;
    return poster || recruiter?.banner_url || trackImages.local;
  };

  return (
    <>
      <Seo
        title="Jobisa Worker Jobs | Skill Identity for Local and Abroad Work"
        description="Build your digital skill identity with Jobisa. Share your video CV, get discovered for India and abroad roles, and start from WhatsApp or the Jobisa app."
        path="/"
      />
      <Navbar isRecruiterSite={false} />

      <main className="bg-white text-slate-900">
        <section className="relative min-h-screen overflow-hidden border-b border-orange-100 bg-white pt-24">
          <SubtleOrangeGrid />
          <div className="absolute top-0 inset-x-0 h-px bg-orange-200/70" />

          <div className="relative z-10 mx-auto w-full max-w-[96rem] px-6 lg:px-12 pt-16 pb-10">
            <div className="grid lg:grid-cols-[1.08fr_0.92fr] gap-10 xl:gap-16 items-center">
              <div className="max-w-[44rem]">
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 px-4 py-2 text-sm font-semibold text-orange-800 mb-8 shadow-sm"
                >
                  <BadgeCheck size={14} />
                  <span>{copy.eyebrow}</span>
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.65, delay: 0.1 }}
                  className="font-black text-slate-950 leading-[1.06] tracking-tight"
                  style={{ fontSize: 'clamp(40px, 5.8vw, 74px)' }}
                >
                  {copy.title}
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="mt-6 max-w-2xl text-[17px] leading-[1.8] text-slate-600"
                >
                  {copy.subtitle}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="mt-8 flex flex-col gap-4 sm:flex-row"
                >
                  <AppDownloadButton
                    href="/download/worker"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#F97316] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(249,115,22,0.24)] transition hover:bg-[#EA580C]"
                  >
                    <Download size={16} />
                    {copy.downloadCta}
                    <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                  </AppDownloadButton>
                  <a
                    href="/worker/login"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-7 py-3.5 text-sm font-semibold text-orange-700 transition hover:border-orange-300 hover:bg-orange-100"
                  >
                    <ArrowRight size={16} />
                    {copy.webCta}
                  </a>
                  <a
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-7 py-3.5 text-sm font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-100"
                  >
                    <MessageCircle size={16} />
                    {copy.whatsappCta}
                  </a>
                </motion.div>

                <div className="mt-8 flex flex-wrap gap-3">
                  {copy.trustPills.map((item) => (
                    <span key={item} className="rounded-full border border-orange-100 bg-white/90 px-3.5 py-1.5 text-sm font-medium text-slate-600 shadow-sm">
                      {item}
                    </span>
                  ))}
                </div>

                <div className="mt-10 border-t border-slate-200 pt-7">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    {copy.recognizedNames}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {TRUST_NAMES.map((name) => (
                    <span key={name} className="rounded-full border border-amber-200 bg-gradient-to-b from-amber-50 to-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm">
                      {name}
                    </span>
                  ))}
                  </div>
                </div>
              </div>

              <div className="relative flex justify-center lg:justify-center">
                <div className="absolute inset-x-8 top-10 h-[78%] rounded-[3rem] bg-[#FFF6EC]" />
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.15 }}
                  className="relative z-10 w-full max-w-[42rem]"
                >
                  <PhoneShowcase theme="worker" />
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        <section id="what-we-do" className="border-b border-slate-200 py-20">
          <div className="mx-auto max-w-[96rem] px-6 lg:px-12">
            <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Trust</p>
                <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950">{copy.trustHeading}</h2>
              </div>
              <div>
                <p className="text-lg leading-8 text-slate-600">{copy.trustCopy}</p>
                <div className="mt-6 space-y-4">
                  {copy.trustBullets.map((item) => (
                    <div key={item} className="flex gap-3 rounded-2xl border border-orange-100 bg-gradient-to-r from-orange-50/70 to-white px-4 py-4">
                      <ShieldCheck className="mt-1 text-orange-500" size={18} />
                      <p className="text-base leading-7 text-slate-700">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-slate-50 py-16">
          <div className="mx-auto max-w-[96rem] px-6 lg:px-12">
            <div className="rounded-3xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-white px-6 py-6 shadow-sm">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-800">Honest Disclaimer</p>
              <p className="mt-3 text-lg leading-8 text-slate-800">
                <strong>{copy.disclaimerTitle}:</strong> {copy.disclaimerBody}
              </p>
            </div>
          </div>
        </section>

        <section id="jobs" className="border-b border-slate-200 py-20">
          <div className="mx-auto max-w-[96rem] px-6 lg:px-12">
            <div className="mb-10 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-500">Live Jobs</p>
                <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950">{copy.jobsTitle}</h2>
                <p className="mt-4 text-lg leading-8 text-slate-600">{copy.jobsBody}</p>
              </div>
              <a
                href="/worker/login?next=%2Fworker%2Fportal"
                className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-5 py-3 text-sm font-semibold text-orange-700 transition hover:border-orange-300 hover:bg-orange-100"
              >
                <Flame size={16} />
                Open more roles on web
              </a>
            </div>

            {liveJobs.length > 0 ? (
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {liveJobs.map((job) => {
                  const campaign = normalizeCampaign(job);
                  const salary = formatSalary(job);
                  const title = job.role || 'Open role';
                  return (
                    <article
                      key={job.id}
                      className="group overflow-hidden rounded-[28px] border border-orange-100 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-[0_20px_45px_-24px_rgba(234,88,12,0.32)]"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={getJobImage(job)}
                          alt={title}
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                        />
                        <div className="absolute inset-0 bg-black/25" />
                        <div className="absolute left-4 right-4 top-4 flex items-center justify-between gap-3">
                          <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-900 shadow-sm">
                            {campaign?.country || 'India'}
                          </span>
                          <div className="flex items-center gap-2">
                            {job.is_urgent ? (
                              <span className="rounded-full bg-[#F97316] px-3 py-1 text-xs font-semibold text-white">
                                Urgent
                              </span>
                            ) : null}
                            {job.job_type ? (
                              <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
                                {job.job_type}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div className="absolute bottom-4 left-4 right-4">
                          <p className="text-sm font-semibold text-white/90">
                            {campaign?.company_name || 'Hiring company'}
                          </p>
                          <h3 className="mt-1 text-2xl font-bold text-white">{title}</h3>
                        </div>
                      </div>

                      <div className="p-6">
                        <div className="flex flex-wrap gap-2">
                          {salary ? (
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
                              {salary}
                            </span>
                          ) : null}
                          {normalizeRecruiter(job)?.is_verified ? (
                            <span className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
                              Verified recruiter
                            </span>
                          ) : null}
                          {job.skill_category ? (
                            <span className="rounded-full bg-orange-50 px-3 py-1 text-sm font-semibold text-orange-700">
                              {job.skill_category}
                            </span>
                          ) : (
                            <span className="rounded-full bg-orange-50 px-3 py-1 text-sm font-semibold text-orange-700">
                              Skill-first
                            </span>
                          )}
                        </div>

                        {job.description ? (
                          <p className="mt-4 line-clamp-3 text-sm leading-7 text-slate-600">{job.description}</p>
                        ) : null}

                        <div className="mt-6 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            {normalizeRecruiter(job)?.logo_url ? (
                              <img
                                src={normalizeRecruiter(job)?.logo_url || ''}
                                alt={campaign?.company_name || 'Company logo'}
                                className="h-11 w-11 rounded-2xl border border-slate-200 object-cover"
                              />
                            ) : (
                              <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-slate-100 text-sm font-bold text-slate-700">
                                {(campaign?.company_name || 'J').charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="text-sm font-semibold text-slate-900">{campaign?.company_name || 'Hiring company'}</p>
                              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">
                                {campaign?.country || 'India'}
                              </p>
                            </div>
                          </div>
                          <a
                            href={`/worker/login?next=${encodeURIComponent(`/worker/portal?job=${job.id}`)}`}
                            className="inline-flex items-center gap-2 rounded-full bg-[#F97316] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#EA580C]"
                          >
                            View & apply
                            <ArrowRight size={15} />
                          </a>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-3xl border border-orange-100 bg-gradient-to-r from-orange-50 to-white px-6 py-6 text-base text-slate-600">
                {copy.jobsEmpty}
              </div>
            )}
          </div>
        </section>

        <section id="why-jobisa" className="border-b border-slate-200 py-20">
          <div className="mx-auto max-w-[96rem] px-6 lg:px-12">
            <div className="mb-10 max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Career Ladder</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950">{copy.tracksTitle}</h2>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                <div className="relative h-64">
                  <img src={trackImages.local} alt={copy.localTitle} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/10 to-transparent" />
                  <span className="absolute left-5 top-5 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-900">India</span>
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-3">
                    <MapPin className="text-blue-600" size={20} />
                    <h3 className="text-2xl font-bold text-slate-950">{copy.localTitle}</h3>
                  </div>
                  <p className="mt-4 text-base leading-8 text-slate-600">{copy.localBody}</p>
                  <AppDownloadButton
                    href="/download/worker"
                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-5 py-3 text-sm font-semibold text-orange-700 transition hover:border-orange-300 hover:bg-orange-100"
                  >
                    <Download size={15} />
                    Explore India roles
                  </AppDownloadButton>
                </div>
              </div>

              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                <div className="relative h-64">
                  <img src={trackImages.abroad} alt={copy.abroadTitle} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/10 to-transparent" />
                  <span className="absolute left-5 top-5 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-900">Gulf / Europe</span>
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="text-emerald-600" size={20} />
                    <h3 className="text-2xl font-bold text-slate-950">{copy.abroadTitle}</h3>
                  </div>
                  <p className="mt-4 text-base leading-8 text-slate-600">{copy.abroadBody}</p>
                  <AppDownloadButton
                    href="/download/worker"
                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-5 py-3 text-sm font-semibold text-orange-700 transition hover:border-orange-300 hover:bg-orange-100"
                  >
                    <Download size={15} />
                    Explore abroad roles
                  </AppDownloadButton>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="border-b border-slate-200 py-20">
          <div className="mx-auto max-w-[96rem] px-6 lg:px-12">
            <div className="mb-10 max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Process</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950">{copy.processTitle}</h2>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
              {copy.processSteps.map((step, index) => {
                const icons = [Video, BadgeCheck, PhoneCall];
                const Icon = icons[index] || ArrowRight;
                return (
                  <div key={step.title} className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
                    <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-400 text-white shadow-sm">
                      <Icon size={20} />
                    </div>
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Step {index + 1}</div>
                    <h3 className="mt-3 text-xl font-bold text-slate-950">{step.title}</h3>
                    <p className="mt-3 text-base leading-7 text-slate-600">{step.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-slate-50 py-20">
          <div className="mx-auto max-w-[96rem] px-6 lg:px-12">
            <div className="grid gap-10 lg:grid-cols-[1fr_0.92fr]">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Authenticity</p>
                <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950">{copy.proofTitle}</h2>
                <p className="mt-5 text-lg leading-8 text-slate-600">{copy.proofBody}</p>
                <div className="mt-6 space-y-4">
                  {copy.proofBullets.map((item) => (
                    <div key={item} className="flex gap-3">
                      <CheckCircle2 className="mt-1 text-emerald-600" size={18} />
                      <p className="text-base leading-7 text-slate-700">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Get Started</p>
                <h3 className="mt-3 text-2xl font-bold text-slate-950">WhatsApp first. Web or app when ready.</h3>
                <p className="mt-4 text-base leading-8 text-slate-600">
                  Workers can start on WhatsApp, upload a short skill video, and then move into either the Jobisa web portal or the app when they are ready. Both stay tied to the same worker profile and application history.
                </p>
                <div className="mt-8 flex flex-col gap-4">
                  <a
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-3.5 text-sm font-semibold text-orange-700 transition hover:border-orange-300 hover:from-orange-100 hover:to-amber-100"
                  >
                    <MessageCircle size={16} />
                    {copy.whatsappCta}
                  </a>
                  <a
                    href="/worker/login"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-6 py-3.5 text-sm font-semibold text-orange-700 transition hover:border-orange-300 hover:bg-orange-100"
                  >
                    <ArrowRight size={16} />
                    {copy.webCta}
                  </a>
                  <AppDownloadButton
                    href="/download/worker"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-600 to-amber-500 px-6 py-3.5 text-sm font-semibold text-white transition hover:from-orange-700 hover:to-amber-600"
                  >
                    <Download size={16} />
                    {copy.downloadCta}
                    <ArrowRight size={15} />
                  </AppDownloadButton>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-[96rem] px-6 lg:px-12">
            <div className="rounded-3xl border border-orange-100 bg-gradient-to-r from-white via-orange-50/40 to-amber-50/60 px-8 py-8 shadow-sm">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Vadodara Root</p>
              <h2 className="mt-3 text-2xl font-bold text-slate-950">{copy.rootTitle}</h2>
              <p className="mt-4 max-w-4xl text-base leading-8 text-slate-600">{copy.rootBody}</p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-5 py-3 text-sm font-semibold text-orange-700 transition hover:border-orange-300 hover:bg-orange-100"
                >
                  <MessageCircle size={15} />
                  Start on WhatsApp
                </a>
                <AppDownloadButton
                  href="/download/worker"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-600 to-amber-500 px-5 py-3 text-sm font-semibold text-white transition hover:from-orange-700 hover:to-amber-600"
                >
                  <Download size={15} />
                  Download Jobisa
                </AppDownloadButton>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default WorkerLandingPage;
